import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@material-ui/core";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";
import moment from "moment";

const ModalEditarPago = ({
  open,
  onClose,
  pago,
  factura,
  onEditar,
  isLoading = false,
}) => {
  const [motivo, setMotivo] = useState("");
  const [metodoPago, setMetodoPago] = useState("");
  const [fechaPago, setFechaPago] = useState(new Date());
  const [numeroCheque, setNumeroCheque] = useState("");
  const [banco, setBanco] = useState("");
  const [error, setError] = useState("");

  // Inicializar valores cuando se abre el modal
  useEffect(() => {
    if (open && pago) {
      setMetodoPago(pago.metodoPago || "");
      setNumeroCheque(pago.numeroCheque || "");
      setBanco(pago.banco || "");

      // Parsear fecha de pago
      const fechaParsed = moment(pago.fechaPago, "DD/MM/YYYY");
      if (fechaParsed.isValid()) {
        setFechaPago(fechaParsed.toDate());
      }
    }
  }, [open, pago]);

  const handleClose = () => {
    setMotivo("");
    setError("");
    onClose();
  };

  const handleEditar = () => {
    // Validaciones
    if (!motivo.trim()) {
      setError("El motivo es obligatorio");
      return;
    }

    if (motivo.length > 500) {
      setError("El motivo no puede exceder 500 caracteres");
      return;
    }

    if (!metodoPago) {
      setError("Debes seleccionar un método de pago");
      return;
    }

    if (metodoPago === "CHEQUE") {
      if (!numeroCheque.trim()) {
        setError("El número de cheque es obligatorio");
        return;
      }
      if (!banco.trim()) {
        setError("El banco es obligatorio");
        return;
      }
    }

    // Detectar cambios
    const cambios = {};
    if (metodoPago !== pago.metodoPago) {
      cambios.metodoPago = metodoPago;
    }

    const fechaNueva = moment(fechaPago).format("DD/MM/YYYY");
    if (fechaNueva !== pago.fechaPago) {
      cambios.fechaPago = fechaNueva;
    }

    if (metodoPago === "CHEQUE") {
      if (numeroCheque !== (pago.numeroCheque || "")) {
        cambios.numeroCheque = numeroCheque;
      }
      if (banco !== (pago.banco || "")) {
        cambios.banco = banco;
      }
    }

    if (Object.keys(cambios).length === 0) {
      setError("No se detectaron cambios en los datos");
      return;
    }

    setError("");
    onEditar({
      id: pago.id,
      motivo: motivo.trim(),
      ...cambios,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="editar-pago-dialog"
    >
      <DialogTitle id="editar-pago-dialog">Editar Pago</DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Información del pago:</strong>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              • <strong>Valor:</strong> {formatCurrency(pago?.valorPesos || 0)}
            </Typography>
            <Typography variant="body2">
              • <strong>Factura:</strong> N° {factura?.folio}
            </Typography>
            <Typography variant="body2">
              • <strong>Registrado por:</strong> {pago?.empleadoRecibePago}
            </Typography>
          </Alert>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Método de Pago *</InputLabel>
              <Select
                value={metodoPago}
                onChange={(e) => setMetodoPago(e.target.value)}
                label="Método de Pago *"
              >
                <MenuItem value="DEPOSITO_BANCARIO">DEPÓSITO BANCARIO</MenuItem>
                <MenuItem value="EFECTIVO">EFECTIVO</MenuItem>
                <MenuItem value="TRANSFERENCIA">TRANSFERENCIA</MenuItem>
                <MenuItem value="TARJETA">TARJETA</MenuItem>
                <MenuItem value="WEBPAY">WEBPAY</MenuItem>
                <MenuItem value="CHEQUE">CHEQUE</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
              <DatePicker
                label="Fecha de Pago *"
                value={fechaPago}
                onChange={(newValue) => setFechaPago(newValue)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth variant="outlined" />
                )}
              />
            </LocalizationProvider>
          </Grid>

          {metodoPago === "CHEQUE" && (
            <>
              <Grid item xs={12}>
                <TextField
                  label="Número de Cheque *"
                  fullWidth
                  variant="outlined"
                  value={numeroCheque}
                  onChange={(e) => setNumeroCheque(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Banco *"
                  fullWidth
                  variant="outlined"
                  value={banco}
                  onChange={(e) => setBanco(e.target.value)}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              label="Motivo de la modificación *"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              error={!!error}
              helperText={error || `${motivo.length}/500 caracteres`}
              placeholder="Describe el motivo de la modificación..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleEditar}
          color="primary"
          variant="contained"
          disabled={isLoading || !motivo.trim()}
          startIcon={isLoading && <CircularProgress size={16} />}
        >
          {isLoading ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEditarPago;
